import * as React from "react";
import { ChevronDownIcon } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Calendar } from "~/components/ui/calendar";
import { Label } from "~/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { CalendarIcon } from "~/icons/CalendarIcon";

interface DatePickerProps {
  value?: Date;
  onChange?: (date: Date | undefined) => void;
  id?: string;
  disabled?: boolean;
  label?: string;
  /** Dates before this (start of day) cannot be selected. */
  minDate?: Date;
  /** Dates after this (end of day) cannot be selected. */
  maxDate?: Date;
}

function startOfDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function endOfDay(d: Date) {
  const s = startOfDay(d);
  s.setHours(23, 59, 59, 999);
  return s;
}

function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

export function DatePicker({
  value,
  onChange,
  id,
  disabled = false,
  label = "von",
  minDate,
  maxDate,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const defaultStartMonth = React.useMemo(
    () => new Date(new Date().getFullYear() - 10, 0, 1),
    []
  );
  const defaultEndMonth = React.useMemo(
    () => new Date(new Date().getFullYear() + 100, 11, 1),
    []
  );

  const startMonth = React.useMemo(() => {
    if (minDate) return startOfMonth(minDate);
    return defaultStartMonth;
  }, [minDate, defaultStartMonth]);

  const endMonth = React.useMemo(() => {
    if (maxDate) return startOfMonth(maxDate);
    return defaultEndMonth;
  }, [maxDate, defaultEndMonth]);

  const disabledMatchers = React.useMemo(() => {
    const matchers: Array<{ before: Date } | { after: Date }> = [];
    if (minDate) matchers.push({ before: startOfDay(minDate) });
    if (maxDate) matchers.push({ after: endOfDay(maxDate) });
    return matchers.length > 0 ? matchers : undefined;
  }, [minDate, maxDate]);

  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (!selectedDate) {
      onChange?.(undefined);
      setOpen(false);
      return;
    }

    // Preserve existing time if we have a value, otherwise use current time
    if (value) {
      selectedDate.setHours(value.getHours());
      selectedDate.setMinutes(value.getMinutes());
    }

    onChange?.(selectedDate);
    setOpen(false);
  };

  return (
    <div className="flex gap-4 w-full">
      <div className="flex gap-3 grow">
        <Label htmlFor={`"date-picker-${id}"`} className="px-1 min-w-[27px]">
          {label}
        </Label>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              id={`"date-picker-${id}"`}
              className="justify-between font-normal w-full"
              disabled={disabled}
            >
              {value ? value.toLocaleDateString() : "Select date"}
              <CalendarIcon className="size-5 -mr-2" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto overflow-hidden p-0" align="start">
            <Calendar
              mode="single"
              selected={value}
              captionLayout="dropdown"
              onSelect={handleDateSelect}
              disabled={disabled ? true : disabledMatchers}
              startMonth={startMonth}
              endMonth={endMonth}
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
